import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";

import { useAppSelector } from "app/hooks";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";

export default function useFirstActionTracking() {
  const hasTrackedFirstAction = useRef(false);
  const durationVitalRef = useRef<any>(null);
  const currentUser = useAppSelector(selectCurrentUser);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const location = useLocation();

  const trackFirstAction = useCallback(
    (
      actionType: string,
      actionName: string,
      context: Record<string, any> = {}
    ) => {
      if (hasTrackedFirstAction.current) {
        return;
      }

      hasTrackedFirstAction.current = true;

      const actionContext = {
        actionType,
        actionName,
        page: location.pathname,
        currentUser: currentUser
          ? {
              id: currentUser.id,
              email: currentUser.email,
              fullName: currentUser.fullName
            }
          : null,
        currentClinic: currentClinic
          ? {
              id: currentClinic.id,
              name: currentClinic.name,
              timezone: currentClinic.timezone
            }
          : null,
        currentPractice: currentPractice
          ? {
              id: currentPractice.id,
              name: currentPractice.name,
              timezone: currentPractice.timezone
            }
          : null,
        timestamp: new Date().toISOString(),
        ...context
      };

      // Stop the duration vital and log the first user action
      if (durationVitalRef.current) {
        datadogRum?.stopDurationVital(durationVitalRef.current);
        durationVitalRef.current = null;
      }

      // Also log detailed action context
      datadogLogs.logger.log("First User Action", actionContext);
    },
    [location.pathname, currentUser, currentClinic, currentPractice]
  );

  // Start timing when page changes and reset tracking
  useEffect(() => {
    hasTrackedFirstAction.current = false;

    // Start duration vital to measure time to first action
    if (!durationVitalRef.current) {
      durationVitalRef.current = datadogRum?.startDurationVital?.(
        "time_to_first_action",
        {
          description: "Time from page load to first user interaction",
          context: {
            page: location.pathname,
            currentUser: currentUser
              ? {
                  id: currentUser.id,
                  email: currentUser.email,
                  fullName: currentUser.fullName
                }
              : null,
            currentClinic: currentClinic
              ? {
                  id: currentClinic.id,
                  name: currentClinic.name,
                  timezone: currentClinic.timezone
                }
              : null,
            currentPractice: currentPractice
              ? {
                  id: currentPractice.id,
                  name: currentPractice.name,
                  timezone: currentPractice.timezone
                }
              : null
          }
        }
      );
    }

    // Cleanup function to stop timing if component unmounts without action
    return () => {
      if (durationVitalRef.current) {
        datadogRum?.stopDurationVital(durationVitalRef.current);
        durationVitalRef.current = null;
      }
    };
  }, [location.pathname, currentUser, currentClinic, currentPractice]);

  return {
    trackFirstAction,
    hasTrackedFirstAction: hasTrackedFirstAction.current
  };
}
